const USER_SUMMARY_RUNTIME_FIELD_CANDIDATES = {
  latestRuntimeAppBuildNumber: [
    "latestRuntimeAppBuildNumber",
    "latest_runtime_app_build_number",
    "runtime_app_build_number",
    "app_build_number",
  ],
  latestRuntimeLocale: [
    "latestRuntimeLocale",
    "latest_runtime_locale",
    "runtime_locale",
    "locale",
  ],
  latestRuntimeTimezoneOffsetBucket: [
    "latestRuntimeTimezoneOffsetBucket",
    "latest_runtime_timezone_offset_bucket",
    "runtime_timezone_offset_bucket",
    "timezone_offset_bucket",
  ],
  latestRuntimeTimezoneNameBucket: [
    "latestRuntimeTimezoneNameBucket",
    "latest_runtime_timezone_name_bucket",
    "runtime_timezone_name_bucket",
    "timezone_name_bucket",
  ],
  latestRuntimeDeviceModelBucket: [
    "latestRuntimeDeviceModelBucket",
    "latest_runtime_device_model_bucket",
    "runtime_device_model_bucket",
    "device_model_bucket",
  ],
  latestRuntimeDeviceModelNameBucket: [
    "latestRuntimeDeviceModelNameBucket",
    "latest_runtime_device_model_name_bucket",
    "runtime_device_model_name_bucket",
    "device_model_name_bucket",
  ],
  latestRuntimeOsMajorBucket: [
    "latestRuntimeOsMajorBucket",
    "latest_runtime_os_major_bucket",
    "runtime_os_major_bucket",
    "os_major_bucket",
  ],
  latestRuntimeOsVersionMinorBucket: [
    "latestRuntimeOsVersionMinorBucket",
    "latest_runtime_os_version_minor_bucket",
    "runtime_os_version_minor_bucket",
    "os_version_minor_bucket",
  ],
  latestRuntimeNetworkTypeBucket: [
    "latestRuntimeNetworkTypeBucket",
    "latest_runtime_network_type_bucket",
    "runtime_network_type_bucket",
    "network_type_bucket",
  ],
  latestRuntimePhysicalDeviceBucket: [
    "latestRuntimePhysicalDeviceBucket",
    "latest_runtime_physical_device_bucket",
    "runtime_physical_device_bucket",
    "physical_device_bucket",
  ],
  latestRuntimeContextAt: [
    "latestRuntimeContextAt",
    "latest_runtime_context_at",
    "runtime_context_at",
    "context_at",
  ],
} as const;

export type AdminUserSummaryRuntimeFieldName = keyof typeof USER_SUMMARY_RUNTIME_FIELD_CANDIDATES;

export type AdminUserSummaryRuntimePayloadStatus =
  | "canonical_runtime_keys_present"
  | "legacy_runtime_keys_present"
  | "runtime_context_only"
  | "runtime_payload_missing";

export type AdminUserSummaryRuntimePayloadAudit = {
  canonicalKeys: string[];
  legacyKeys: string[];
  normalizedValues: Partial<Record<AdminUserSummaryRuntimeFieldName, unknown>>;
  status: AdminUserSummaryRuntimePayloadStatus;
};

function hasOwn(input: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(input, key);
}

export function normalizeAdminUserSummaryRuntimePayload<T extends Record<string, unknown>>(input: T): T {
  let changed = false;
  const next: Record<string, unknown> = { ...input };

  for (const [canonicalKey, candidates] of Object.entries(USER_SUMMARY_RUNTIME_FIELD_CANDIDATES)) {
    if (next[canonicalKey] !== undefined) {
      continue;
    }

    for (const candidateKey of candidates) {
      if (candidateKey === canonicalKey) {
        continue;
      }
      if (hasOwn(input, candidateKey) && input[candidateKey] !== undefined) {
        next[canonicalKey] = input[candidateKey];
        changed = true;
        break;
      }
    }
  }

  return changed ? (next as T) : input;
}

export function auditAdminUserSummaryRuntimePayload(input: Record<string, unknown>): AdminUserSummaryRuntimePayloadAudit {
  const canonicalKeys: string[] = [];
  const legacyKeys: string[] = [];
  const normalizedValues: Partial<Record<AdminUserSummaryRuntimeFieldName, unknown>> = {};

  for (const [canonicalKey, candidates] of Object.entries(USER_SUMMARY_RUNTIME_FIELD_CANDIDATES) as Array<
    [AdminUserSummaryRuntimeFieldName, readonly string[]]
  >) {
    for (const [candidateIndex, candidateKey] of candidates.entries()) {
      if (!hasOwn(input, candidateKey) || input[candidateKey] === undefined) {
        continue;
      }
      normalizedValues[canonicalKey] = input[candidateKey];
      if (candidateIndex === 0) {
        canonicalKeys.push(candidateKey);
      } else {
        legacyKeys.push(candidateKey);
      }
      break;
    }
  }

  const hasDetailedRuntime = (
    normalizedValues.latestRuntimeAppBuildNumber !== undefined ||
    normalizedValues.latestRuntimeLocale !== undefined ||
    normalizedValues.latestRuntimeTimezoneOffsetBucket !== undefined ||
    normalizedValues.latestRuntimeTimezoneNameBucket !== undefined ||
    normalizedValues.latestRuntimeDeviceModelBucket !== undefined ||
    normalizedValues.latestRuntimeDeviceModelNameBucket !== undefined ||
    normalizedValues.latestRuntimeOsMajorBucket !== undefined ||
    normalizedValues.latestRuntimeOsVersionMinorBucket !== undefined ||
    normalizedValues.latestRuntimeNetworkTypeBucket !== undefined ||
    normalizedValues.latestRuntimePhysicalDeviceBucket !== undefined
  );

  const status: AdminUserSummaryRuntimePayloadStatus =
    canonicalKeys.length > 0
      ? "canonical_runtime_keys_present"
      : legacyKeys.length > 0
        ? "legacy_runtime_keys_present"
        : normalizedValues.latestRuntimeContextAt !== undefined
          ? "runtime_context_only"
          : "runtime_payload_missing";

  return {
    canonicalKeys,
    legacyKeys,
    normalizedValues,
    status: hasDetailedRuntime ? status : normalizedValues.latestRuntimeContextAt !== undefined ? "runtime_context_only" : status,
  };
}
